/**
 * Formula Engine - Database-driven calculation system
 * 
 * Centralizes all ROI/NPV/Payback calculations using formulas from database tables:
 * - formula_library: Stores mathematical expressions
 * - calculation_rules: Business logic and conditional rules  
 * - variables_registry: Variable definitions and sources
 */

interface FormulaResult {
  value: number;
  success: boolean;
  error?: string;
}

interface VariableMap {
  [key: string]: number;
}

export class FormulaEngine {
  private formulas: Map<string, any> = new Map();
  private variables: Map<string, any> = new Map();
  private calculationRules: Map<string, any> = new Map();
  private loading = false;
  private loaded = false;

  constructor() {
    // Defer loading until an app JWT is available to avoid 401 floods
    const token = typeof window !== 'undefined' ? localStorage.getItem('jwt_token') : null;
    if (token) {
      this.loadFromDatabase();
    } else if (typeof window !== 'undefined') {
      const trigger = () => {
        window.removeEventListener('auth:token', trigger);
        this.loadFromDatabase();
      };
      window.addEventListener('auth:token', trigger);
    }
  }

  /**
   * Load formulas, variables, and rules from database
   */
  private async loadFromDatabase() {
    if (this.loaded || this.loading) return;
    const token = typeof window !== 'undefined' ? localStorage.getItem('jwt_token') : null;
    if (!token) {
      // No token yet; wait for 'auth:token' event instead of spamming unauthenticated requests
      return;
    }
    this.loading = true;
    try {
      const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {} as Record<string, string>;
      // Load formulas from financial intelligence API
      const formulaResponse = await fetch('/api/financial-intelligence/formulas', { headers: authHeaders });
      if (formulaResponse.ok) {
        const formulas = await formulaResponse.json();
        formulas.forEach((formula: any) => {
          this.formulas.set(formula.name, {
            expression: formula.expression,
            variablesUsed: formula.variables_used || [],
            returnType: formula.return_type,
            category: formula.category,
            displayName: formula.display_name
          });
        });
        console.log('🧮 FORMULA ENGINE: Loaded', formulas.length, 'formulas from database');
      }

      // Load variables from financial intelligence API
      const variablesResponse = await fetch('/api/financial-intelligence/variables', { headers: authHeaders });
      if (variablesResponse.ok) {
        const variables = await variablesResponse.json();
        variables.forEach((variable: any) => {
          this.variables.set(variable.name, {
            displayName: variable.display_name,
            dataType: variable.data_type,
            category: variable.category,
            sourceScreen: variable.source_screen,
            defaultValue: variable.default_value,
            isRequired: variable.is_required
          });
        });
        console.log('🧮 FORMULA ENGINE: Loaded', variables.length, 'variables from database');
      }

      // Load calculation rules
      const rulesResponse = await fetch('/api/admin/calculation-rules', { headers: authHeaders });  
      if (rulesResponse.ok) {
        const rules = await rulesResponse.json();
        rules.forEach((rule: any) => {
          this.calculationRules.set(rule.name, {
            formula: rule.formula,
            variables: rule.variables,
            description: rule.description,
            isActive: rule.is_active
          });
        });
        console.log('🧮 FORMULA ENGINE: Loaded', rules.length, 'calculation rules from database');
      }
    } catch (error) {
      console.error('🧮 FORMULA ENGINE: Failed to load from database:', error);
    } finally {
      this.loading = false;
      this.loaded = true;
    }
  }

  /**
   * Evaluate a formula by name with provided variables
   */
  public evaluateFormula(formulaName: string, variableValues: VariableMap): FormulaResult {
    const formula = this.formulas.get(formulaName);
    if (!formula) {
      return {
        value: 0,
        success: false,
        error: `Formula '${formulaName}' not found in database`
      };
    }

    try {
      // Validate required variables are provided
      const missingVars = formula.variablesUsed.filter((varName: string) => 
        !(varName in variableValues) || variableValues[varName] === undefined || variableValues[varName] === null
      );

      if (missingVars.length > 0) {
        console.warn(`🧮 FORMULA ENGINE: Missing variables for ${formulaName}:`, missingVars);
        return {
          value: 0,
          success: false,
          error: `Missing variables: ${missingVars.join(', ')}`
        };
      }

      // Evaluate the mathematical expression
      const result = this.evaluateExpression(formula.expression, variableValues);
      
      console.log(`🧮 FORMULA ENGINE: ${formulaName} =`, result, 'using variables:', variableValues);
      
      return {
        value: result,
        success: true
      };
    } catch (error) {
      console.error(`🧮 FORMULA ENGINE: Error evaluating ${formulaName}:`, error);
      return {
        value: 0,
        success: false,
        error: `Evaluation error: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Safely evaluate mathematical expressions
   * Supports: +, -, *, /, ^, (), basic functions
   */
  private evaluateExpression(expression: string, variables: VariableMap): number {
    // Replace variable names with their values
    let processedExpression = expression;
    
    Object.entries(variables).forEach(([varName, value]) => {
      // Use word boundaries to prevent partial replacements
      const regex = new RegExp(`\\b${varName}\\b`, 'g');
      processedExpression = processedExpression.replace(regex, value.toString());
    });

    // Handle power operator (^) -> convert to Math.pow
    processedExpression = processedExpression.replace(/\(([^)]+)\)\s*\^\s*\(([^)]+)\)/g, 'Math.pow($1, $2)');
    processedExpression = processedExpression.replace(/(\w+|\([^)]+\))\s*\^\s*(-?\d+\.?\d*)/g, 'Math.pow($1, $2)');

    // Safely evaluate using Function constructor (limited scope)
    try {
      const func = new Function('Math', `return ${processedExpression}`);
      const result = func(Math);
      
      if (typeof result !== 'number' || isNaN(result)) {
        throw new Error('Expression did not evaluate to a valid number');
      }
      
      return result;
    } catch (error) {
      throw new Error(`Invalid expression: ${processedExpression}`);
    }
  }

  /**
   * Calculate all financial metrics using database formulas
   */
  public calculateFinancialMetrics(valueSummary: any): any {
    console.log('🧮 FORMULA ENGINE: Calculating financial metrics from valueSummary');
    
    // Extract variables from valueSummary blob
    const variables = this.extractVariables(valueSummary);
    console.log('🧮 FORMULA ENGINE: Extracted variables:', variables);

    const results: any = {};

    // Calculate ROI Percentage
    const roiResult = this.evaluateFormula('ROI Percentage', variables);
    if (roiResult.success) {
      results.roiPercentage = roiResult.value;
    }

    // Calculate Net Present Value
    const npvResult = this.evaluateFormula('Net Present Value', variables);
    if (npvResult.success) {
      results.netPresentValue = npvResult.value;
    }

    // Calculate Payback Period
    const paybackResult = this.evaluateFormula('Payback Period', variables);
    if (paybackResult.success) {
      results.paybackPeriod = paybackResult.value;
    }

    // Calculate Five Year Value
    const fiveYearResult = this.evaluateFormula('Five Year Value', variables);
    if (fiveYearResult.success) {
      results.fiveYearValue = fiveYearResult.value;
    }

    console.log('🧮 FORMULA ENGINE: Calculated metrics:', results);
    return results;
  }

  /**
   * Extract variables from valueSummary for formula evaluation
   */
  private extractVariables(valueSummary: any): VariableMap {
    const variables: VariableMap = {};

    // Extract annual benefit from task FTE allocation
    const taskFTE = valueSummary?.taskFTEAllocation;
    if (taskFTE?.fteMatrix && taskFTE?.automationPercentages && taskFTE?.fteCostsByRegion) {
      let totalAnnualSavings = 0;
      
      // Calculate annual savings from FTE data
      Object.entries(taskFTE.fteMatrix).forEach(([activity, regionFTEs]: [string, any]) => {
        const automationPercent = taskFTE.automationPercentages[activity] || 0;
        
        Object.entries(regionFTEs).forEach(([region, fteCount]: [string, any]) => {
          const regionKey = region === 'North America' ? 'americas' : 
                          region === 'Shared Services' ? 'shared_services' : 
                          region.toLowerCase();
          const fteCost = taskFTE.fteCostsByRegion[region] || taskFTE.fteCostsByRegion[regionKey] || 0;
          const activitySavings = (fteCount * (automationPercent / 100) * fteCost * 250); // 250 working days
          totalAnnualSavings += activitySavings;
        });
      });
      
      variables.annualBenefit = totalAnnualSavings;
    }

    // Extract investment from ROI data or pricing
    const roiData = valueSummary?.roi_data;
    if (roiData?.dynamicMetrics?.tco) {
      variables.investment = roiData.dynamicMetrics.tco;
    }

    // Extract discount rate (default 8% if not specified)
    variables.discountRate = roiData?.discountRate || 0.08;
    
    // Set years for calculations
    variables.years = 5;

    return variables;
  }

  /**
   * Get formula by name (for debugging/admin)
   */
  public getFormula(name: string) {
    return this.formulas.get(name);
  }

  /**
   * List all available formulas
   */
  public listFormulas(): string[] {
    return Array.from(this.formulas.keys());
  }
}

// Singleton instance
export const formulaEngine = new FormulaEngine();
